import { DataType } from '@mode-switch/express';
import { SingleColorSwatchComponent } from '@mode/colors/ui';
import { ChartTypes } from '@mode/shared/contract-common';
import { MenuItem } from '@mode/shared/ui';
import {
  canColTypeUseFormatter,
  currencyPresetOptions,
  currencySymbolOptions,
  decimalSymbolOptions,
  displayUnitOptions,
  getDefaultTextFormat,
  getTextFormatByCurrencyPreset,
  getTextFormatOptionsByShortcutType,
  linkColorOptions,
  negativeBehaviorOptions,
  newLinkStyleOptions,
  thousandsGroupingOptions,
  thousandsSeparatorOptions,
} from '@mode/shared/util-js';

export type CurrencyPresetMenuItem = MenuItem<Partial<ChartTypes.TextFormat.CurrencyType>>;
export type DisplayUnitMenuItem = MenuItem<ChartTypes.DisplayUnit>;
export type ThousandsSeparatorMenuItem = MenuItem<ChartTypes.DigitSeparator>;
export type ThousandsGroupingMenuItem = MenuItem<ChartTypes.DigitGrouping>;
export type DecimalSeparatorMenuItem = MenuItem<ChartTypes.DigitSeparator>;
export type NegativeBehaviorMenuItem = MenuItem<ChartTypes.NegativeBehavior>;
export type SymbolMenuItem = MenuItem;
export type UrlDisplayMenuItem = MenuItem<ChartTypes.LinkStyle>;
export type UrlLinkColorMenuItem = MenuItem<ChartTypes.LinkColor>;

export const CurrencyPresetMenuItems: Array<CurrencyPresetMenuItem> = currencyPresetOptions.map(
  (tableFormattingOption) => {
    return {
      name: tableFormattingOption.label,
      value: {
        currencyPreset: tableFormattingOption.value,
        ...getTextFormatByCurrencyPreset(tableFormattingOption.value),
      },
      desc: tableFormattingOption.example,
    };
  }
);

export const DisplayUnitMenuItems: Array<DisplayUnitMenuItem> = displayUnitOptions.map((displayUnit) => {
  return {
    name: displayUnit.label,
    value: displayUnit.value,
    desc: displayUnit.example,
  };
});

export const ThousandsSeparatorMenuItems: Array<ThousandsSeparatorMenuItem> = thousandsSeparatorOptions.map(
  (option) => {
    return {
      name: option.label,
      value: option.value,
    };
  }
);

export const ThousandsGroupingMenuItems: Array<ThousandsGroupingMenuItem> = thousandsGroupingOptions.map((option) => {
  return {
    name: option.label,
    value: option.value,
  };
});

export const DecimalSeparatorMenuItems: Array<DecimalSeparatorMenuItem> = decimalSymbolOptions.map((option) => {
  return {
    name: option.label,
    value: option.value,
  };
});

export const NegativeBehaviorMenuItems: Array<NegativeBehaviorMenuItem> = negativeBehaviorOptions
  .slice()
  .reverse()
  .map((option) => {
    return {
      name: option.label,
      value: option.value,
    };
  });

export const SymbolMenuItems: Array<SymbolMenuItem> = currencySymbolOptions
  .map((option) => {
    return {
      name: option.label,
      value: option.value,
    };
  })
  .concat([
    { name: 'Custom', value: 'custom' },
    { name: 'None', value: '' },
  ]);

export const UrlDisplayMenuItems: Array<UrlDisplayMenuItem> = newLinkStyleOptions.map((option) => {
  return {
    name: option.label,
    value: option.value,
  };
});

const LinkColors: Record<string, string> = {
  'mode-blue': '#3d70d4',
  'mode-purple': '#7456ce',
  'mode-green': '#2a6f2b',
  'mode-orange': '#e9a41f',
  'mode-pink': '#fb8492',
  blue: '#0177a3',
  purple: '#a96dcb',
  green: '#37af66',
  orange: '#ff6b1b',
  pink: '#d9577a',
  black: '#000000',
};

export const UrlLinkColorItems: Array<UrlLinkColorMenuItem> = linkColorOptions.map((option) => {
  const inputs = new Map<string, unknown>();
  inputs.set('color', LinkColors[option.value]);

  return {
    name: option.label,
    value: option.value,
    decorator: {
      component: SingleColorSwatchComponent,
      inputs,
    },
  };
});

export function findOrFirst<T>(items: Array<MenuItem<T>>, value: T): MenuItem<T> {
  return items.find((item) => item.value === value) ?? items[0];
}

/**
 * Precision must be an integer value between 0..12 inclusive
 */
export function isValidPrecision(value: unknown): value is number {
  if (Number.isInteger(value)) {
    return <number>value >= 0 && <number>value <= 12;
  }

  return false;
}

export function extractColumnReferences(urlAddress: string) {
  const columns: string[] = [];

  const urlParts = urlAddress.split('{');

  urlParts.forEach((part: string) => {
    if (part.indexOf('}') > -1) {
      const column = part.split('}')[0];
      columns.push(column);
    }
  });

  return columns;
}

export function resolveInitialFormat(colType: DataType.Name, format?: ChartTypes.TextFormat) {
  if (format == null || !canColTypeUseFormatter(format.type, colType)) {
    format = getBaseFormatByDataType(colType);
  }

  const shortcutType = ChartTypes.FormatShortcutType.Custom;
  return {
    ...format,
    shortcutType,
    colType,
  };
}

export function getBaseFormatByDataType(
  type: DataType.Name.Integer | DataType.Name.Float | DataType.Name.Decimal
): ChartTypes.TextFormat.NumberType;
export function getBaseFormatByDataType(
  type: DataType.Name.DateTime | DataType.Name.Timestamp
): ChartTypes.TextFormat.DateType;
export function getBaseFormatByDataType(
  type: DataType.Name.Boolean | DataType.Name.String | DataType.Name.Regexp | DataType.Name.Null
): ChartTypes.TextFormat.DefaultType;
export function getBaseFormatByDataType(type: DataType.Name): ChartTypes.TextFormat;
export function getBaseFormatByDataType(type: DataType.Name): ChartTypes.TextFormat {
  switch (type) {
    case DataType.Name.Float:
    case DataType.Name.Integer:
    case DataType.Name.Decimal:
    case DataType.Name.Numeric:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Numeric);
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.DateAndTime);
    default: {
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.PlainText);
    }
  }
}

export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.Numeric
): ChartTypes.TextFormat.NumberType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.Currency
): ChartTypes.TextFormat.CurrencyType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.Percent
): ChartTypes.TextFormat.PercentType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.DateAndTime
): ChartTypes.TextFormat.DateType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.Url
): ChartTypes.TextFormat.UrlType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType.PlainText
): ChartTypes.TextFormat.DefaultType;
export function getBaseFormatByShortcutType(
  shortcutType: ChartTypes.FormatShortcutType
): ChartTypes.TextFormat | undefined {
  switch (shortcutType) {
    case ChartTypes.FormatShortcutType.Numeric:
      return {
        ...getDefaultTextFormat(DataType.Name.Float),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Number,
        precision: undefined,
      };
    case ChartTypes.FormatShortcutType.Currency:
      return {
        ...getDefaultTextFormat(DataType.Name.Float),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Financial,
        precision: undefined,
      };
    case ChartTypes.FormatShortcutType.Percent:
      return {
        ...getDefaultTextFormat(DataType.Name.Float),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Percent,
        precision: undefined,
      };
    case ChartTypes.FormatShortcutType.DateAndTime:
      return {
        ...getDefaultTextFormat(DataType.Name.DateTime),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Date,
      };
    case ChartTypes.FormatShortcutType.Url:
      return {
        ...getDefaultTextFormat(DataType.Name.String),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Url,
      };
    case ChartTypes.FormatShortcutType.PlainText:
      return {
        ...getDefaultTextFormat(DataType.Name.String),
        ...getTextFormatOptionsByShortcutType(shortcutType),
        shortcutType: ChartTypes.FormatShortcutType.Custom,
        type: ChartTypes.FormatType.Default,
      };
  }
}

export function getBaseFormatByFormatType(type: ChartTypes.FormatType.Number): ChartTypes.TextFormat.NumberType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType.Financial): ChartTypes.TextFormat.CurrencyType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType.Percent): ChartTypes.TextFormat.PercentType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType.Date): ChartTypes.TextFormat.DateType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType.Url): ChartTypes.TextFormat.UrlType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType): ChartTypes.TextFormat.DefaultType;
export function getBaseFormatByFormatType(type: ChartTypes.FormatType): ChartTypes.TextFormat {
  switch (type) {
    case ChartTypes.FormatType.Number:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Numeric);
    case ChartTypes.FormatType.Financial:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Currency);
    case ChartTypes.FormatType.Percent:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Percent);
    case ChartTypes.FormatType.Date:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.DateAndTime);
    case ChartTypes.FormatType.Url:
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Url);
    default: {
      return getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.PlainText);
    }
  }
}
